import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeModeProvider, useThemeMode } from "./contexts/ThemeContext";
/* 🔹 MUI */
import { ThemeProvider, CssBaseline } from "@mui/material";
import { getTheme } from "./theme";
/* 🔹 Fonts (Inter weights) */
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@fontsource/inter/900.css";

// Conditionally import inspection tools (development only)
// Use no-op components as defaults - will be replaced if inspector is available
let InspectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;
let InspectionTooltip: React.FC = () => null;
let InspectionHighlight: React.FC = () => null;
let InspectionOverlays: React.FC = () => null;
let setupInterceptors: () => void = () => {};

// Only try to import in development, and gracefully handle if package is missing
if (process.env.NODE_ENV === "development") {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const inspector = require("@zargaryanvh/react-component-inspector");
    if (inspector) {
      InspectionProvider = inspector.InspectionProvider || InspectionProvider;
      InspectionTooltip = inspector.InspectionTooltip || InspectionTooltip;
      InspectionHighlight = inspector.InspectionHighlight || InspectionHighlight;
      InspectionOverlays = inspector.InspectionOverlays || InspectionOverlays;
      setupInterceptors = inspector.setupInterceptors || setupInterceptors;
    }
  } catch (error) {
    // Inspector package not available (e.g., in CI/CD) - use no-op components
    // This is expected in production builds or when package is not installed
  }
}

// Suppress non-critical console errors from browser extensions and Firebase
const originalError = console.error;
console.error = (...args: any[]) => {
  // Convert all args to string for checking, handling Error objects
  const errorMessage = args
    .map(arg => {
      if (arg instanceof Error) {
        return arg.message + " " + arg.stack;
      }
      return String(arg);
    })
    .join(" ");
  
  // Suppress ERR_BLOCKED_BY_CLIENT errors (browser extensions blocking Firestore)
  if (errorMessage.includes("ERR_BLOCKED_BY_CLIENT") || 
      errorMessage.includes("net::ERR_BLOCKED_BY_CLIENT")) {
    // Silently ignore - these are from browser extensions (ad blockers)
    return;
  }
  
  // Suppress Cross-Origin-Opener-Policy warnings (Firebase Auth popup)
  // These are harmless warnings when Firebase Auth checks if popup was closed
  if (errorMessage.includes("Cross-Origin-Opener-Policy") ||
      errorMessage.includes("Cross-Origin-Opener-Policy policy would block") ||
      errorMessage.includes("window.closed") ||
      errorMessage.includes("window.close") ||
      errorMessage.includes("popup.ts")) {
    // Silently ignore - these are cosmetic warnings from Firebase Auth popup
    return;
  }
  
  // Suppress browser extension errors (webpage_content_reporter.js is from Opera/Chrome extensions)
  if (errorMessage.includes("webpage_content_reporter.js") ||
      errorMessage.includes("Unexpected token 'export'") ||
      errorMessage.includes("chrome-extension://") ||
      errorMessage.includes("moz-extension://")) {
    // Silently ignore - these are from browser extensions interfering with the page
    return;
  }
  
  // Suppress expected Firestore permission errors (users can't read other users' documents)
  // These are handled gracefully by falling back to member data
  if (errorMessage.includes("Missing or insufficient permissions") &&
      (errorMessage.includes("Error fetching user doc") || 
       errorMessage.includes("users/"))) {
    // Silently ignore - these are expected when trying to read other users' documents
    // The code handles this by using member data instead
    return;
  }
  
  // Suppress known React DOM sync errors (portals / inspector / MUI unmount race)
  if (errorMessage.includes("removeChild") && errorMessage.includes("not a child of this node")) {
    return;
  }

  // NEVER suppress critical initialization errors
  if (errorMessage.includes("Root element not found") ||
      errorMessage.includes("Failed to initialize app") ||
      errorMessage.includes("CRITICAL") ||
      errorMessage.includes("App failed to mount") ||
      (errorMessage.includes("Cannot read properties") && errorMessage.includes("root"))) {
    // Always log critical errors
    originalError.apply(console, args);
    return;
  }
  
  // Log all other errors normally
  originalError.apply(console, args);
};

// Suppress non-critical console warnings
const originalWarn = console.warn;
console.warn = (...args: any[]) => {
  // Convert all args to string for checking, handling Error objects
  const warningMessage = args
    .map(arg => {
      if (arg instanceof Error) {
        return arg.message + " " + arg.stack;
      }
      return String(arg);
    })
    .join(" ");
  
  // Suppress Cross-Origin-Opener-Policy warnings (Firebase Auth popup)
  if (warningMessage.includes("Cross-Origin-Opener-Policy") ||
      warningMessage.includes("Cross-Origin-Opener-Policy policy would block") ||
      warningMessage.includes("window.closed") ||
      warningMessage.includes("window.close") ||
      warningMessage.includes("popup.ts")) {
    // Silently ignore - these are cosmetic warnings from Firebase Auth popup
    return;
  }
  
  // Suppress browser extension warnings (webpage_content_reporter.js is from Opera/Chrome extensions)
  if (warningMessage.includes("webpage_content_reporter.js") ||
      warningMessage.includes("chrome-extension://") ||
      warningMessage.includes("moz-extension://")) {
    // Silently ignore - these are from browser extensions interfering with the page
    return;
  }
  
  // Log all other warnings normally
  originalWarn.apply(console, args);
};

// Service worker: in development unregister so dev sees latest build; in production register for fast PWA resume
if ("serviceWorker" in navigator) {
  if (process.env.NODE_ENV === "development") {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        console.log("Unregistering service worker:", registration.scope);
        registration.unregister();
      });
    });
  } else {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js", { scope: "/" }).then(
        (reg) => {
          console.log("Service worker registered for PWA resume cache");
          reg.update();
        },
        (err) => console.warn("Service worker registration failed:", err)
      );
    });
  }
}

// Clear caches only in development so dev sees latest build; production keeps shell cache
if (process.env.NODE_ENV === "development" && "caches" in window) {
  caches.keys().then((names) => {
    names.forEach((name) => {
      console.log("Clearing cache:", name);
      caches.delete(name);
    });
  });
}

/* Wrapper that provides MUI theme from theme mode context */
function AppThemeWrapper({ children }: { children: React.ReactNode }): React.ReactElement {
  const { themeMode } = useThemeMode();
  const theme = React.useMemo(() => getTheme(themeMode), [themeMode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

// Error boundary for initialization errors
const initializeApp = () => {
  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      console.error("❌ CRITICAL: Root element not found!");
      document.body.innerHTML = '<div style="padding: 20px; font-family: sans-serif;"><h1>App Initialization Error</h1><p>Root element not found. Please refresh the page.</p></div>';
      return;
    }

    // Clear any existing content in root (in case of stale state)
    rootElement.innerHTML = "";

    const root = ReactDOM.createRoot(rootElement);

    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <BrowserRouter>
            <ThemeModeProvider>
              <AppThemeWrapper>
                <InspectionProvider>
                  <App />
                  <InspectionOverlays />
                </InspectionProvider>
              </AppThemeWrapper>
            </ThemeModeProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>
    );

    // Verify app mounted successfully after a delay that allows React 18 Strict Mode
    // unmount/remount and concurrent commit to finish (avoids false "empty root" on /admin redirect).
    const mountCheckDelay = 2500;
    setTimeout(() => {
      if (rootElement.children.length === 0) {
        // One retry after a short delay in case commit was slightly delayed
        setTimeout(() => {
          if (rootElement.children.length === 0) {
            console.error("❌ CRITICAL: App failed to mount! Root element is empty after render.");
            console.error("This might be due to a React error. Check the console for details.");
          } else {
            console.log("✅ App initialized successfully");
          }
        }, 500);
      } else {
        console.log("✅ App initialized successfully");
      }
    }, mountCheckDelay);

    reportWebVitals();
  } catch (error) {
    console.error("❌ CRITICAL: Failed to initialize app:", error);
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="padding: 20px; font-family: sans-serif;">
          <h1>App Initialization Error</h1>
          <p>Failed to start the application. Please refresh the page.</p>
          <p style="color: red; font-size: 12px;">Error: ${error instanceof Error ? error.message : String(error)}</p>
          <button onclick="window.location.reload(true)" style="padding: 10px 20px; margin-top: 10px; cursor: pointer;">
            Reload Page
          </button>
        </div>
      `;
    }
  }
};

// Setup inspection interceptors (dev-only)
if (process.env.NODE_ENV === "development") {
  try {
    setupInterceptors();
  } catch (error) {
    // Silently fail if inspector is not available
  }
}

// Initialize app
initializeApp();

// Enable Hot Module Replacement (HMR) for better development experience
if (process.env.NODE_ENV === "development") {
  // @ts-ignore - webpack hot module replacement
  if (module.hot) {
    // @ts-ignore
    module.hot.accept();
  }
}
